import React, { useState } from "react";
import { Button, Descriptions, Empty, List, Modal, Pagination, Skeleton, Table, theme } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import { useOutletContext } from "react-router-dom";
import useIsMobile from "../../hooks/useIsMobile";
import useBusinessResource from "../../hooks/useBusinessResource";
import BusinessResourceNotice from "../BusinessResourceNotice";
import "./catalog-workspace.css";

export function useCatalogStyle() {
  const { token } = theme.useToken();
  return { "--catalog-bg": token.colorBgContainer, "--catalog-fill": token.colorFillAlter,
    "--catalog-text": token.colorText, "--catalog-secondary": token.colorTextSecondary,
    "--catalog-heading": token.colorTextHeading, "--catalog-border": token.colorBorderSecondary,
    "--catalog-success": token.colorSuccessText, "--catalog-success-bg": token.colorSuccessBg,
    "--catalog-error": token.colorErrorText };
}

export default function CatalogWorkspace({ title, subtitle, action, children }) {
  const mobile = useIsMobile();
  const outlet = useOutletContext();
  const style = useCatalogStyle();
  return <div className="catalog-workspace" style={style}>
    <header className="catalog-header">
      <div className="catalog-heading-row">
        {mobile && outlet?.openMobileMenu && <Button icon={<MenuOutlined />} aria-label="Ouvrir le menu" onClick={outlet.openMobileMenu} />}
        <div><h1>{title}</h1><p>{subtitle}</p></div>
      </div>
      {action}
    </header>
    {children}
  </div>;
}

export function CatalogModal({ title, subtitle, children, className = "", ...props }) {
  const style = useCatalogStyle();
  return <Modal centered destroyOnClose width={1280} {...props} style={{ ...style, ...props.style }}
    className={`catalog-modal ${className}`} title={<div className="catalog-modal-heading"><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>}>
    {children}
  </Modal>;
}

export function CatalogTable({ columns, dataSource, onDetail, pagination, loading, rowSelection, ...props }) {
  const mobile = useIsMobile();
  const [page, setPage] = useState(1);
  if (mobile) {
    const size = pagination?.pageSize || 20;
    const current = pagination?.current || page;
    const serverPaged = Boolean(pagination?.onChange);
    const rows = serverPaged || pagination === false ? dataSource : dataSource.slice((current - 1) * size, current * size);
    return <div className="catalog-mobile-list">
      <List loading={loading} dataSource={rows} locale={{ emptyText: "Aucun résultat pour ces filtres." }}
        renderItem={(record) => <List.Item className="catalog-mobile-record">
          <button type="button" className="catalog-mobile-record__detail" onClick={() => onDetail?.(record)}>
            {columns.filter((column) => column.key !== "actions").map((column, index) => <span className={`catalog-mobile-field ${index === 0 ? "catalog-mobile-field--primary" : ""}`} key={column.key || column.dataIndex}>
              <span>{column.title}</span><strong>{column.render ? column.render(record[column.dataIndex], record) : record[column.dataIndex] || "—"}</strong>
            </span>)}
          </button>
          <div className="catalog-mobile-record__actions">{columns.find((column) => column.key === "actions")?.render(null, record)}</div>
        </List.Item>} />
      {pagination !== false && <Pagination current={current} pageSize={size} total={pagination?.total ?? dataSource.length}
        showSizeChanger={false} size="small" onChange={(next) => { setPage(next); pagination?.onChange?.(next); }} />}
    </div>;
  }
  return <Table {...props} className={`catalog-table ${props.className || ""}`} rowKey="id" tableLayout="fixed"
    columns={columns.map((column) => ({ ellipsis: true, ...column }))} dataSource={dataSource} loading={loading}
    rowSelection={rowSelection} pagination={pagination === false ? false : { pageSize: 20, showSizeChanger: false, ...pagination }}
    onRow={(record) => ({ onClick: () => onDetail?.(record), tabIndex: onDetail ? 0 : undefined,
      onKeyDown: (event) => { if (event.target === event.currentTarget && ["Enter", " "].includes(event.key)) { event.preventDefault(); onDetail?.(record); } } })} />;
}

export function CatalogDetailsModal({ open, recordId, title, resourceName, load, scope, render, onClose, onEdit, canEdit }) {
  const resource = useBusinessResource({ ...scope, scopeLevel: "company", moduleScope: "commercial", resource: resourceName,
    parameters: [recordId], enabled: Boolean(open && recordId), active: open, load: (signal) => load(recordId, signal) });
  const denied = [401, 403, 404].includes(resource.error?.response?.status);
  const record = denied ? null : resource.data;
  return <CatalogModal open={open} onCancel={onClose} title={title} width={850}
    footer={<><Button onClick={onClose}>Fermer</Button>{canEdit && <Button type="primary"
      disabled={!record || resource.status !== "verified" || resource.loading}
      onClick={() => { onClose(); onEdit(record); }}>Modifier</Button>}</>}>
    <BusinessResourceNotice resource={denied ? { ...resource, data: undefined } : resource} label="le détail" />
    {record ? render(record) : resource.loading ? <Skeleton active /> : !resource.error && <Empty description="Les informations ne sont plus disponibles." />}
  </CatalogModal>;
}

export function CatalogFacts({ items }) {
  return <Descriptions column={{ xs: 1, sm: 2 }} layout="vertical" items={items.map(([label, children], index) => ({ key: index, label, children: children ?? "—" }))} />;
}
